import { showNotification } from '@mantine/notifications';
import { useEffect, useMemo, useState, createContext, useContext } from 'react';
import { useRouter } from 'next/router';
import { first, isNonNullish, isString, partition, pipe } from 'remeda';
import { NotNull, parseOrDefault } from 'shared/type';
import { compactString } from 'shared/text';
import { doSafe, doTry } from 'shared/promise';
import { filterObj, fromEntries } from 'shared/collection';
import { isNullOrEmpty, keyBy, notNullOrEmpty } from 'shared/collection';
import { uri } from 'shared/uri';
import { useMemoValue } from 'shared/ui/useMemo';
import { useQueryClient } from '@tanstack/react-query';
import { zProfileRow, type FetchProfileProps, zProfileCfg, ProfileCfg, ProfileCfgGlobal, zProfileCfgGlobal } from './profileTypes';
import { PortalStatus, zPortalQuery, UserMd, zUserMd } from './portalTypes';
import { fetchProfiles, profileAvatarAbsolute } from './profile';
import { MyProfile, ProfileUI } from './profileTypes';
import { DateTime } from 'luxon';
import { useQueryEx, useQuerySbRead, useSbChannel } from 'utils/db/useQuerySb';
import { showError } from 'components/notify';
import { listMods } from 'components/hooks/useUIState';
import { useQueryString } from 'utils/useQueryString';
import { saveActivity } from './activity';
import { User as SbUser } from '@supabase/supabase-js';
import { sb } from 'utils/db';
import { useLocalStorage, useSessionStorage } from '@mantine/hooks';
import posthog from 'posthog-js';
import { authDataFromToken, workspaceRolesEqual } from 'utils/db/jwtRoles';
import type { License } from 'shared/forecast/license';
import { Crisp } from 'crisp-sdk-web';
import { env } from 'utils/env';
import * as jwt from 'jsonwebtoken';
const parseUser = (u: SbUser) => ({
  ...u,
  user_metadata: parseOrDefault(zUserMd, u.user_metadata, {} as UserMd)!
});

/** Load the profile data for this user. If not in returns null */
export const fetchPortal = async ({
  anon,
  invite
}: {
  debugName?: string;
  anon?: boolean;
} & FetchProfileProps = {}) => {
  let getSeshRes = await sb.auth.getSession().then(({
    data,
    error
  }) => {
    if (error) {
      console.error(error, 'error getting session');
      return {
        error
      };
    }
    const getToken = () => sb.auth.getSession()?.then(s => s.data?.session?.access_token);
    const tokenObject = data.session?.access_token ? authDataFromToken(data.session.access_token) : undefined;
    // if (data.session?.access_token) {
    //   const decoded = jwt.decode(data.session.access_token) as Record<string, any> | null
    //   if (decoded) console.debug(`set request.jwt.claim to '${JSON.stringify(decoded)}';`)
    // }
    return {
      user: data?.session?.user ?? undefined,
      error: undefined,
      getToken,
      tokenObject
    };
  });
  let rawUser = getSeshRes.user;
  let error: Error | undefined = getSeshRes.error;
  if (!rawUser) {
    if (!(invite && anon)) return null; // only sign in anon if the invite link is anon. Anon could be fake, but it will be checked by getProfiles
    const res = await sb.auth.signInAnonymously();
    if (res.error || res.data?.user == null) {
      error = new Error(`error signing in anonymously${res.error ? `: ${res.error.message}` : ''}`);
      return null;
    }
    rawUser = res.data.user;
  }
  if (!rawUser) return null;
  const fetchedProfiles = await fetchProfiles(sb, {
    invite
  }).catch(e => {
    const message = 'message' in e && isString(e.message) ? e.message : undefined;
    if (message == 'TypeError: Failed to fetch') throw new Error('Unable to connect to the server');
    throw new Error(`Unable to fetch profiles: ${message}`);
  });
  const profiles = fetchedProfiles?.filter(p => p.ok);
  const profileErrors = fetchedProfiles?.filter(p => !p.ok);
  const profileWsRoles = profiles ? fromEntries(profiles.map(p => [p.workspace_id, p.roles])) : undefined;
  const jwtWsRoles = getSeshRes.tokenObject?.workspace_roles;
  if (profiles?.some(p => p.is_new) || profileWsRoles && jwtWsRoles && !workspaceRolesEqual(profileWsRoles, jwtWsRoles)) {
    console.log('refreshing session because it something changed');
    await sb.auth.refreshSession();
  }

  // because anon users don't have a verified email, there is no point to showing them the request screen. Treat invite missing/error as a fatal error.
  if (anon && invite && !profiles?.some(p => p.workspace_id == invite.workspace)) {
    const profileError = profileErrors?.find(e => e.workspace_id == invite.workspace);
    error = profileError ? new Error(profileError.error) : new Error('Invite not found (it may have expired or been disabled)');
    if (anon) {
      await sb.auth.signOut();
      throw error;
    }
  }
  const user = parseUser(rawUser);
  return {
    user,
    profiles,
    error,
    getToken: getSeshRes.getToken,
    profileErrors
  };
};
export type PortalUser = NotNull<Awaited<ReturnType<typeof fetchPortal>>>;
export const usePortalLoad = () => {
  const qs = useQueryString(zPortalQuery);
  const {
    query
  } = qs;
  const {
    data: portal,
    error: portalPanic,
    refetch: portalRefetch,
    setQueryData: setPortal
  } = useQueryEx({
    queryKey: ['portal'],
    queryFn: () => fetchPortal({
      invite: query.invite && query.workspace ? {
        workspace: query.workspace,
        id: query.invite
      } : undefined,
      anon: query.anon
    }).then(p => {
      if (p?.user) {
        const profile = p.profiles?.find(p_0 => p_0.workspace_id == query.workspace);
        if (posthog.__loaded) posthog.capture('Authenticated', {
          workspace_id: query.workspace,
          is_anonymous: p.user.is_anonymous,
          user_id: p.user.id,
          email: p.user.email,
          workspaceRoles: p.profiles?.map(p_1 => `${p_1.workspace_id}: ${p_1.roles.toSorted().join('|')}`).join(', ')
        });
        if (env.NEXT_PUBLIC_CRISP_WEBSITE_ID) {
          if (p.user.email) Crisp.user.setEmail(p.user.email);
          if (profile) {
            Crisp.user.setNickname(profile.name);
            if (notNullOrEmpty(profile.avatar_url)) {
              const avatarUrl = profileAvatarAbsolute(profile.avatar_url!);
              doTry(() => Crisp.user.setAvatar(avatarUrl));
            }
            if (profile.workspace.name) {
              Crisp.user.setCompany(profile.workspace.name, {});
            }
          } else if (p.user.user_metadata.name) {
            Crisp.user.setNickname(p.user.user_metadata.name);
          }
        }
      }
      if (typeof window !== 'undefined') {
        // after logging in, remove invite, auth tokens and other things
        const queryPropsToRemove = ['invite', 'anon', 'email', 'emailInvite'];
        if (p?.user && queryPropsToRemove.some(prop => prop in query)) qs.setQuery({
          invite: undefined,
          anon: undefined,
          email: undefined,
          emailInvite: undefined
        });
      }
      return p;
    }),
    staleTime: Infinity,
    retry: false
  });
  const portalError = portalPanic?.message ?? portal?.error?.message;
  const updatePortal = (p_2: Partial<PortalUser>) => {
    if (!portal) return;
    setPortal({
      ...portal,
      ...p_2
    });
  };
  const status: PortalStatus = portalError && !portal ? 'error' : portal === undefined ? 'unknown' : portal === null ? 'un-authenticated' : 'authenticated';
  const profiles = useMemo(() => doSafe(portal?.profiles, ps => {
    const [globals, normals] = partition(ps, p_3 => p_3.workspace_id == 'global');
    const global = doSafe(globals?.[0], g => ({
      ...g,
      ...zProfileCfgGlobal.parse(g)
    })); // union type??
    return {
      normals,
      global,
      forSelect: [...(normals ?? []), global].filter(isNonNullish).filter(p_4 => notNullOrEmpty(p_4.roles))
    };
  }), [portal]);
  const storageWorkspace = doSafe(typeof localStorage != 'undefined' ? localStorage : undefined, s => s.getItem('workspace')) ?? undefined;
  const wsProfile = (ws: string | undefined): MyProfile | undefined => ws == null ? undefined : profiles?.forSelect.find(p_5 => p_5.workspace_id == ws);
  const [portalRefetchWorkspace, setPortalRefetchWorkspace] = useState<string>();
  // work out what profile to use out of the ones we have loaded for this user
  const profile_1 = useMemo(() => {
    if (!portal) return undefined;
    const profile_0 = query.workspace ? wsProfile(query.workspace) : wsProfile(storageWorkspace) ?? first(profiles?.forSelect ?? []);
    if (profile_0) return profile_0;
    // we have loaded the portal, but don't have access to a specific workspace. It could be new so try again
    if (!query.workspace) return undefined;
    if (portalRefetchWorkspace == query.workspace) return undefined;
    setPortalRefetchWorkspace(query.workspace);
    portalRefetch();
    return undefined;
  }, [query.workspace, storageWorkspace, portal]);
  useEffect(() => {
    if (profile_1 && portal?.user && sessionStorage.getItem('identify-id') != portal.user.id) {
      if (posthog.__loaded) posthog.identify(portal.user.id, {
        profileId: profile_1.id,
        workspaceId: profile_1.workspace_id,
        profileName: profile_1.name,
        workspace: profile_1.workspace.name
      });
      sessionStorage.setItem('identify-id', portal.user.id);
    }
    const shouldRecord = profile_1 != null && profile_1.workspace.logActivity;
    if (posthog.__loaded) {
      if (shouldRecord && !posthog.sessionRecordingStarted()) posthog.startSessionRecording(); // do this in case. But session recording is automatically started with the current settings
      if (!shouldRecord && posthog.sessionRecordingStarted()) posthog.stopSessionRecording();
    }
  }, [profile_1]);

  // get this workspaces basic profile info for the ui centrally because its use all the time
  const {
    data: _profilesUI,
    refetch: refetchProfilesUI
  } = useQuerySbRead({
    table: 'profile',
    select: 'id, deleted, workspace_id, cfg',
    // even though we are only using some columns and flatten them, keep original shape for this query so te sync works out of the box
    schema: zProfileRow.pick({
      id: true,
      deleted: true,
      workspace_id: true
    }).extend({
      cfg: zProfileCfg.pick({
        avatar_url: true,
        name: true,
        roles: true
      })
    }).strip(),
    filter: {
      workspace_id: profile_1?.workspace_id,
      deleted: false
    },
    options: {
      enabled: profile_1?.workspace_id != null,
      staleTime: Infinity
    }
  });
  const profilesUI: Record<string, ProfileUI> | undefined = useMemo(() => doSafe(_profilesUI, ps_0 => keyBy(ps_0.map(({
    id,
    cfg
  }) => ({
    id,
    ...cfg
  }) satisfies ProfileUI), p_6 => p_6.id)), [_profilesUI]);

  // load a channel with workspace to reflect those changes
  // on first load of the portal, fetch the portal data
  // const portalLoading = useRef(false)
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = sb.auth.onAuthStateChange(event => {
      if (['SIGNED_OUT', 'USER_DELETED'].includes(event)) setPortal(null);
      // else if (!portalLoading.current) fetchPortal(sb).then(setPortal)
    });
    return () => subscription.unsubscribe();
  }, [portal]);

  // subscribe to profile changes for the current user to update local state immediately
  useSbChannel({
    table: 'profile',
    filter: portal?.user?.id ? ['user_id', portal.user.id] : undefined,
    onChange: p_7 => {
      if (!portal?.profiles) return;
      if (p_7.eventType === 'DELETE' && p_7.old?.id) {
        // Remove deleted profile from local state
        const newProfiles = portal.profiles.filter(prof => prof.id !== p_7.old.id);
        updatePortal({
          profiles: newProfiles
        });
      } else if (p_7.eventType === 'UPDATE' && p_7.new?.id) {
        const profileId = p_7.new.id;
        const existingProfile = portal.profiles.find(prof_0 => prof_0.id === profileId);
        if (p_7.new.deleted) {
          // Profile was deleted, remove from local state
          const newProfiles_0 = portal.profiles.filter(prof_1 => prof_1.id !== profileId);
          updatePortal({
            profiles: newProfiles_0
          });
        } else if (existingProfile) {
          // Profile was updated, merge the changes
          // Parse the cfg from the update
          const updatedCfg = zProfileCfg.parse(p_7.new.cfg ?? {});
          const updatedProfile = {
            ...existingProfile,
            ...updatedCfg,
            workspace_id: p_7.new.workspace_id ?? existingProfile.workspace_id
          };
          const newProfiles_1 = portal.profiles.map(prof_2 => prof_2.id === profileId ? updatedProfile : prof_2);
          updatePortal({
            profiles: newProfiles_1
          });
        } else {
          // Profile was updated but we don't have it locally yet, refetch portal
          portalRefetch();
        }
      } else if (p_7.eventType === 'INSERT' && p_7.new?.id) {
        // New profile was created, refetch portal to get full enriched data
        portalRefetch();
      }
    },
    debugName: 'portal-profile',
    disabled: status !== 'authenticated' || !portal?.user?.id
  });

  // if it is the first time loading the portal, set the session workspace if it's explicitly on the url
  useEffect(() => {
    if (query.workspace != null && seshWorkspace != query.workspace) {
      setWorkspace(query.workspace); // set the workspace if it changes
    }
  }, [query.workspace]);
  const [noPrompt, setNoPrompt] = useSessionStorage<boolean | undefined>({
    key: 'no-prompt',
    defaultValue: undefined
  });
  useEffect(() => {
    if (query.noPrompt != null && query.noPrompt != noPrompt) setNoPrompt(query.noPrompt);
  }, [query.noPrompt]);
  const qc = useQueryClient();
  const logOut = async (options?: {
    email?: string;
  }) => {
    qc.clear();
    const {
      error
    } = await sb.auth.signOut();
    setPortal(null);
    if (posthog.__loaded) posthog.reset();
    if (error) showNotification({
      color: 'red',
      title: 'error when signing out',
      message: error.message
    });
    if (options?.email) qs.setQuery({
      email: options.email
    });else window.location.reload();
  };
  const [seshWorkspace, setSeshWorkspace] = useLocalStorage<string | undefined>({
    key: 'workspace',
    defaultValue: undefined
  });
  const setWorkspace = async (workspace_id: string) => {
    setSeshWorkspace(workspace_id);
    localStorage.setItem('workspace', workspace_id);
  };
  const refreshWorkspaces = async () => {
    setSeshWorkspace(undefined);
    portalRefetch();
  };

  /** updates the current profile and also updates the local state */
  const updateProfile = async (values: Partial<ProfileCfg | ProfileCfgGlobal>, options_0?: {
    id?: string;
    noNotify?: boolean;
  }) => {
    if (!portal) throw new Error('no portal');
    const id_0 = options_0?.id ?? profile_1?.id;
    if (!id_0) throw new Error('no profile id');
    const existingProfile_0 = portal.profiles?.find(p_8 => p_8.id == id_0);
    if (!existingProfile_0) throw new Error('no profile found');
    await sb.rpc('update_cfg', {
      table_name: 'profile',
      id: id_0,
      profile_id: id_0,
      cfg_updates: values,
      updated_at: DateTime.utc().toISO()!
    }).then(res => {
      if (res.error) {
        console.error(res.error, 'error updating profile');
        return;
      }
      if (!options_0?.noNotify) showNotification({
        message: 'profile saved'
      });
      const newProfiles_2 = listMods(portal.profiles ?? []).upsert({
        ...existingProfile_0,
        ...values
      });
      updatePortal({
        profiles: newProfiles_2
      }); // local state
    });
  };
  const updateProfileGlobal = async (values_0: Partial<ProfileCfgGlobal>) => {
    if (!profiles?.global) throw new Error('no global profile');
    await updateProfile(values_0, {
      id: profiles.global.id,
      noNotify: true
    });
  };
  const url = typeof window == 'undefined' ? undefined : uri(window.location.toString());
  const activity = useMemoValue(url ? {
    type: 'view' as const,
    path: compactString(url.parts.path?.join('/')),
    query: pipe(url.parts.query,
    // we don't want to log most query, but keep adding to this to log it
    q => filterObj(q ?? {}, k => isString(k) && ['tab'].includes(k)), q_0 => isNullOrEmpty(q_0) ? undefined : q_0)
  } : undefined);
  useEffect(() => {
    if (!profile_1 || !activity) return;
    saveActivity({
      activity,
      profile: profile_1,
      sb
    });
  }, [activity, profile_1?.id, profile_1?.workspace_id]);
  const updateUserMetadata = async (data: Partial<UserMd>) => {
    if (!portal) throw new Error('no portal');
    const res_0 = await sb.auth.updateUser({
      data
    });
    if (res_0.error) {
      showError(res_0.error, 'error updating user metadata');
      return;
    }
    updatePortal({
      user: parseUser(res_0.data.user)
    });
  };
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const capture = (event_0: string, props?: Record<string, any>) => {
    if (!(posthog as any).__loaded) return;
    if (!profile_1?.workspace?.logActivity) return;
    posthog.capture(event_0, props);
  };
  return {
    status,
    ...portal,
    updateUserMetadata,
    logOut,
    capture,
    profile: profile_1,
    updateProfile,
    updateProfileGlobal,
    profiles,
    tz,
    setWorkspace,
    refreshWorkspaces,
    profilesUI,
    // we base status of the portalError above, and then give all the errors back so the UI can decide what to show nicely
    portalError: portalError ?? portal?.profileErrors?.find(e => query.workspace != null && e.workspace_id == query.workspace)?.error,
    portalRefetch,
    refetchProfilesUI,
    workspace_id: profile_1?.workspace_id,
    workspace: profile_1?.workspace,
    noPrompt,
    readonly: profile_1?.readonly,
    license: profile_1?.license
  };
};
export type UsePortal = ReturnType<typeof usePortalLoad>;
// react context for portal
export const PortalContext = createContext<Omit<UsePortal, 'profile' | 'workspace_id' | 'user' | 'profiles' | 'getToken' | 'readonly'> & {
  getToken: () => Promise<string | undefined>;
  profile: NotNull<UsePortal['profile']>;
  workspace: NotNull<UsePortal['profile']>['workspace'];
  workspace_id: string;
  user: PortalUser['user'];
  profiles: NotNull<UsePortal['profiles']>;
  readonly: boolean;
  license: License | undefined;
}>({} as any);
export const usePortal = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error("usePortal must be used within a PortalProvider");
  }
  return context;
};

// export type User = NonNullable<UsePortal['user']>